export interface StepVideo { id: string; url: string; poster_url: string; duration: number; width: number; height: number }
export interface VideoMaterial { id: string; name: string; size: number; status: 'uploading' | 'queued' | 'processing' | 'ready' | 'failed'; error_code?: string; video: StepVideo }
export interface Credential { upload_url: string; upload_method: string; upload_headers: Record<string, string> }
export type VideoRequest = <T>(path: string, body?: unknown) => Promise<T>;
export function validateVideoFile(file: File) {
 if (!file.size || file.size > 100 * 1024 * 1024) throw new Error('视频不能为空，且不能超过 100 MB');
 const ext = file.name.split('.').pop()?.toLowerCase();
 if (ext !== 'mp4' && ext !== 'mov') throw new Error('请选择 MP4 或 MOV 视频');
 return ext === 'mov' ? 'video/quicktime' : 'video/mp4';
}
export function safeVideoURL(value?: string) { try { const u = new URL(value || ''); return u.protocol === 'https:' && !u.username && !u.password ? u.href : undefined; } catch { return undefined; } }
export function videoError(item: VideoMaterial) { return item.error_code === 'unsupported_video' ? '视频内容无效，或超过 5 分钟 / 4096 像素限制，请更换文件' : '视频处理失败，请重试处理或更换文件'; }
function abortError() { return new DOMException('已取消', 'AbortError'); }
function delay(ms: number, signal: AbortSignal) { return new Promise<void>((resolve, reject) => { if (signal.aborted) { reject(abortError()); return; } const done = () => { signal.removeEventListener('abort', abort); resolve(); }; const timer = setTimeout(done, ms); const abort = () => { clearTimeout(timer); reject(abortError()); }; signal.addEventListener('abort', abort, { once: true }); }); }
export function putVideo(file: File, credential: Credential, signal: AbortSignal, progress: (n: number) => void) {
 return new Promise<void>((resolve, reject) => {
  if (signal.aborted) { reject(abortError()); return; }
  const xhr = new XMLHttpRequest(); const abort = () => xhr.abort(); const clean = () => signal.removeEventListener('abort', abort);
  xhr.open(credential.upload_method, credential.upload_url); xhr.timeout = 10 * 60_000;
  Object.entries(credential.upload_headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
  xhr.upload.onprogress = e => { if (e.lengthComputable) progress(Math.round(e.loaded / e.total * 100)); };
  xhr.onload = () => { clean(); xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('视频上传失败，请到视频素材库确认状态后重试')); };
  xhr.onerror = xhr.ontimeout = () => { clean(); reject(new Error('视频上传连接中断，请到视频素材库确认状态后重试')); };
  xhr.onabort = () => { clean(); reject(abortError()); };
  signal.addEventListener('abort', abort, { once: true }); xhr.send(file);
 });
}
export async function waitForVideo(request: VideoRequest, base: string, id: string, signal: AbortSignal, phase: (s: string) => void) {
 for (let i = 0; i < 240; i++) {
  if (signal.aborted) throw abortError();
  const item = await request<VideoMaterial>(`${base}/${id}`);
  if (signal.aborted) throw abortError();
  if (item.status === 'ready') return item;
  if (item.status === 'failed') throw new Error(videoError(item));
  phase(item.status === 'processing' ? '正在处理视频并生成封面…' : '视频已上传，等待处理…');
  await delay(3000, signal);
 }
 throw new Error('视频仍在处理中，可稍后从视频素材库选择');
}
export async function uploadTaskVideo(request: VideoRequest, base: string, file: File, signal: AbortSignal, progress: (n: number) => void, phase: (s: string) => void) {
 const mime = validateVideoFile(file);
 phase('正在创建上传…');
 const result = await request<{ item: VideoMaterial; credential: Credential }>(base, { name: file.name, size: file.size, mime, request_key: crypto.randomUUID() });
 if (signal.aborted) throw abortError();
 phase('正在上传视频…');
 await putVideo(file, result.credential, signal, progress);
 if (signal.aborted) throw abortError();
 await request(`${base}/${result.item.id}/complete`, {});
 return waitForVideo(request, base, result.item.id, signal, phase);
}
